import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { getContrastTextColor, hexToRgb, mixColors, rgbToHex, Neutral100 } from '@src/utils/colors';
import { TOP_BRANDS_LIMIT } from './alexa-visibility-constants';
import {
  groupBy,
  isSameBrand,
  normalizeBrand,
  rankCompetitorBrands
} from './alexa-visibility-utils';

export const ComparisonFraming = {
  FAVORABLE: 'favorable',
  NEUTRAL: 'neutral',
  UNFAVORABLE: 'unfavorable'
} as const;
export type ComparisonFramingType = (typeof ComparisonFraming)[keyof typeof ComparisonFraming];

// Best first: also the tie-break order wherever one framing has to be picked.
const FRAMING_ORDER: ComparisonFramingType[] = [
  ComparisonFraming.FAVORABLE,
  ComparisonFraming.NEUTRAL,
  ComparisonFraming.UNFAVORABLE
];

const getFraming = (result: AlexaVisibilityResult): ComparisonFramingType | undefined =>
  FRAMING_ORDER.find(framing => framing === result.classification?.competitiveFraming);

/**
 * The responses where Alexa put the brand against others: framed favorably, neutrally or
 * unfavorably. A response with no comparison ("not applicable") isn't one.
 */
export const getCompetitiveComparisons = (
  results: AlexaVisibilityResult[]
): AlexaVisibilityResult[] => results.filter(result => getFraming(result) !== undefined);

export interface FramingSummary {
  total: number;
  favorable: number;
  neutral: number;
  unfavorable: number;
}

/** How many comparisons were framed each way; the three always add up to `total`. */
export const getFramingSummary = (comparisons: AlexaVisibilityResult[]): FramingSummary => {
  const count = (framing: ComparisonFramingType): number =>
    comparisons.filter(result => getFraming(result) === framing).length;
  return {
    total: comparisons.length,
    favorable: count(ComparisonFraming.FAVORABLE),
    neutral: count(ComparisonFraming.NEUTRAL),
    unfavorable: count(ComparisonFraming.UNFAVORABLE)
  };
};

export const ComparisonVerdict = {
  WINS: 'wins',
  LOSES: 'loses',
  MIXED: 'mixed'
} as const;
export type ComparisonVerdictType = (typeof ComparisonVerdict)[keyof typeof ComparisonVerdict];

/**
 * Whether the brand wins its comparisons: it wins when at least half are favorable, loses when
 * more are unfavorable than favorable, and is otherwise mixed (mostly neutral).
 */
export const getComparisonVerdict = (summary: FramingSummary): ComparisonVerdictType => {
  if (summary.total > 0 && summary.favorable * 2 >= summary.total) return ComparisonVerdict.WINS;
  if (summary.unfavorable > summary.favorable) return ComparisonVerdict.LOSES;
  return ComparisonVerdict.MIXED;
};

export interface ComparedBrand {
  brand: string;
  count: number;
  // Share of all the comparisons, 0-100.
  percent: number;
}

/** The brands Alexa mentions alongside the target in comparisons, most often first. */
export const getMostComparedAgainst = (
  comparisons: AlexaVisibilityResult[],
  targetBrand: string,
  limit = 5
): ComparedBrand[] => {
  const byBrand = new Map<string, { brand: string; count: number }>();
  comparisons.forEach(result => {
    const seen = new Set<string>();
    (result.classification?.brandsMentioned ?? []).forEach(brand => {
      const key = normalizeBrand(brand);
      if (!key || seen.has(key) || isSameBrand(brand, targetBrand)) return;
      seen.add(key);
      const entry = byBrand.get(key) ?? { brand: brand.trim(), count: 0 };
      entry.count += 1;
      byBrand.set(key, entry);
    });
  });
  return Array.from(byBrand.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map(entry => ({
      ...entry,
      percent: comparisons.length > 0 ? (entry.count / comparisons.length) * 100 : 0
    }));
};

export interface DrivingQuery {
  query: string;
  comparisons: number;
  // The framing most of this query's comparisons had.
  framing: ComparisonFramingType;
}

/** The queries that trigger the most comparisons, with how they were mostly framed. */
export const getDrivingQueries = (
  comparisons: AlexaVisibilityResult[],
  limit = 5
): DrivingQuery[] =>
  Array.from(groupBy(comparisons, result => result.query).entries())
    .map(([query, queryComparisons]) => {
      const summary = getFramingSummary(queryComparisons);
      const framing =
        [...FRAMING_ORDER].sort((a, b) => summary[b] - summary[a])[0] ?? ComparisonFraming.NEUTRAL;
      return { query, comparisons: queryComparisons.length, framing };
    })
    .sort((a, b) => b.comparisons - a.comparisons || a.query.localeCompare(b.query))
    .slice(0, limit);

export interface MatrixCell {
  brand: string;
  count: number;
  total: number;
  // Share of this query's responses that mention the brand, 0-100.
  percent: number;
}

export interface MatrixRow {
  query: string;
  cells: MatrixCell[];
}

/** The target brand first, then the competitors Alexa mentions most (as on the Query Overview). */
export const getMatrixBrands = (
  results: AlexaVisibilityResult[],
  targetBrand: string,
  limit = TOP_BRANDS_LIMIT
): string[] => [targetBrand, ...rankCompetitorBrands(results, targetBrand).slice(0, limit - 1)];

/**
 * Every query (not just the top ones) with the share of its responses that mention each brand,
 * highest first for the first brand, then A to Z.
 */
export const getVisibilityMatrix = (
  results: AlexaVisibilityResult[],
  brands: string[]
): MatrixRow[] =>
  Array.from(groupBy(results, result => result.query).entries())
    .map(([query, queryResults]) => ({
      query,
      cells: brands.map(brand => {
        const count = queryResults.filter(result =>
          result.classification?.brandsMentioned?.some(mentioned => isSameBrand(mentioned, brand))
        ).length;
        return {
          brand,
          count,
          total: queryResults.length,
          percent: queryResults.length > 0 ? (count / queryResults.length) * 100 : 0
        };
      })
    }))
    .sort(
      (a, b) =>
        (b.cells[0]?.percent ?? 0) - (a.cells[0]?.percent ?? 0) || a.query.localeCompare(b.query)
    );

const HEAT_MIN_STRENGTH = 0.2;

/**
 * A matrix cell's colors: the brand's own color, faint at 0% and full at 100%, with text that
 * stays readable on it.
 */
export const getHeatCellColors = (
  brandColor: string,
  percent: number
): { backgroundColor: string; color: string } => {
  const strength =
    HEAT_MIN_STRENGTH + (1 - HEAT_MIN_STRENGTH) * Math.max(0, Math.min(1, percent / 100));
  const backgroundColor = rgbToHex(mixColors(hexToRgb(Neutral100), hexToRgb(brandColor), strength));
  return { backgroundColor, color: getContrastTextColor(backgroundColor) };
};

/** One row per competitive response (not just the ones shown), for the Excel export. */
export const buildCompetitiveCitationExportRows = (
  comparisons: AlexaVisibilityResult[]
): Record<string, unknown>[] =>
  [...comparisons]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map(result => ({
      Query: result.query,
      'Scraped At': result.createdAt,
      Framing: result.classification?.competitiveFraming ?? '',
      Rank: result.classification?.targetBrandRank ?? '',
      'Brands Compared': (result.classification?.brandsMentioned ?? []).join('; '),
      Excerpt: result.classification?.rawExcerpt ?? result.response ?? ''
    }));
